import { createGlobalStyle } from "styled-components";

export const GlobalStyle = createGlobalStyle`

*,*::before,*::after{
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

h1,h2,h3,h4,h5,h6{
    margin: 0;
    padding: 0;
}

html {
    scroll-behavior: smooth;
}

@media (prefers-reduced-motion: reduce) {
    html {
        scroll-behavior: auto;
    }
}

body{
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #ffffff;
    -webkit-tap-highlight-color: transparent;
    touch-action: pan-y;
}

:root{
    // Typography scale - Desktop
    --fontBig: 6em;
    --fontxxxl: 4.5em;
    --fontxxl: 3rem;
    --fontxl: 2.25rem;
    --fontlg: 1.5rem;
    --fontmd: 1.125rem;
    --fontsm: 1rem;
    --fontxs: 0.875rem;
    --fontxxs: 0.75rem;

    @media screen and (max-width: 48em) {
        --fontBig: 3em;
        --fontxxxl: 2.5rem;
        --fontxxl: 2rem;
        --fontxl: 1.75rem;
        --fontlg: 1.25rem;
        --fontmd: 1rem;
        --fontsm: 0.9375rem;
        --fontxs: 0.8125rem;
        --fontxxs: 0.6875rem;
    }

    @media screen and (max-width: 30em) {
        --fontBig: 2.5em;
        --fontxxxl: 2rem;
        --fontxxl: 1.75rem;
        --fontxl: 1.5rem;
        --fontlg: 1.125rem;
        --fontmd: 0.9375rem;
        --fontsm: 0.875rem;
        --fontxs: 0.75rem;
        --fontxxs: 0.625rem;
    }

    // Colors - White/Black Professional Palette
    --black: #000000;
    --blackSoft: #0a0a0a;
    --blackSecondary: #111111;
    --grey: #525252;
    --greyLight: #a1a1a1;
    --greyMuted: #717171;
    --greySoft: #e5e5e5;
    --white: #ffffff;
    --whiteSoft: #fafafa;
    
    // Glass effects - Liquid Glass
    --glassBg: rgba(255, 255, 255, 0.7);
    --glassBgDark: rgba(0, 0, 0, 0.7);
    --glassBorder: rgba(255, 255, 255, 0.18);
    --glassBorderDark: rgba(0, 0, 0, 0.18);
    --glassShadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    --glassShadowDark: 0 8px 32px rgba(0, 0, 0, 0.3);
    --glassBlur: blur(20px) saturate(180%);

    // Fonts
    --fontL: "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    --fontR: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

    // Landing page palette
    --ink-900: #0a0a0b;
    --ink-800: #141416;
    --ink-700: #232327;
    --paper: #fbfbf9;
    --paper-2: #f2f2ee;
    --line: #e4e4de;
    --text-hi: #0a0a0b;
    --text-lo: #6b6b70;
    --text-inv: #fafaf8;
    --green: #15b87f;
    --violet: #7c5cff;
    --amber: #e8a33d;
    
    // Spacing
    --space-xs: 0.5rem;
    --space-sm: 1rem;
    --space-md: 1.5rem;
    --space-lg: 2rem;
    --space-xl: 3rem;
    --space-2xl: 4rem;
    
    // Border radius
    --radius-sm: 12px;
    --radius-md: 16px;
    --radius-lg: 20px;
    --radius-xl: 24px;
    --radius-2xl: 32px;
    --radius-full: 9999px;
    
    // Transitions
    --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-base: 250ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: 400ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* Keep anchored targets clear of the fixed header */
[id] {
    scroll-margin-top: 5rem;
}

/* One-shot scroll reveal, driven by useReveal (IntersectionObserver) */
[data-reveal] {
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 400ms cubic-bezier(.16, 1, .3, 1), transform 400ms cubic-bezier(.16, 1, .3, 1);
    transition-delay: calc(var(--reveal-i, 0) * 60ms);
}

[data-reveal].is-visible {
    opacity: 1;
    transform: none;
}

@media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
        animation-duration: .01ms !important;
        transition-duration: .01ms !important;
    }
}

::selection {
    background: rgba(0, 0, 0, 0.2);
    color: var(--black);
}

`
